import { getApiClient } from '../../axios-config';
import Logger from '../../../../utils/logger';

/**
 * Project Documents Assistant API Service
 *
 * Thin wrapper over the FieldTime-owned Project Documents Assistant API
 * (routes/ai/project-docs/assistant-route.js). The browser never calls
 * RAG directly -- every method here calls a human-authenticated FieldTime
 * endpoint only, and FieldTime is what securely calls RAG server-side.
 *
 * A conversation carries two separate discipline fields (Migration 073),
 * both REQUIRED-BUT-NULLABLE wherever they appear in a request/response
 * body: `disciplineId` is the conversation's IMMUTABLE INITIAL
 * classification (set once at creation; drives ConversationSidebar's
 * badge) and `searchScopeDisciplineId` is its MUTABLE CURRENT retrieval
 * scope (drives the composer selector and what the backend forwards to
 * RAG). `null` means project-wide ("GEN COND" in the UI); a UUID names a
 * specific discipline. Every outgoing body below that includes one of
 * these keys does so with a plain, unconditional key -- never a
 * falsy-conditional spread -- so an explicit `null` is always sent as
 * `null`, never silently omitted (which the backend treats as a distinct,
 * rejected "key missing" case, see assistant-controller.js's
 * requireNullableUuidKey).
 */
class AssistantAPI {
  /**
   * Create a new conversation for a project, scoped to a specific
   * discipline or project-wide.
   * @param {string} projectId
   * @param {string|null} disciplineId - null for project-wide/"GEN COND"
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created conversation object
   */
  static async createConversation(projectId, disciplineId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations`,
      { disciplineId }
    );
    return response.data.data.conversation;
  }

  /**
   * List every conversation for a project, most recently active first --
   * across every discipline (including project-wide/null-scoped ones).
   * disciplineId is no longer a filter here at all (Migration 073 follow-
   * up): each returned conversation still carries its own disciplineId.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of conversation objects
   */
  static async listConversations(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations`
    );
    Logger.debug('AssistantAPI.listConversations - Response structure', {
      hasData: !!response.data?.data,
      conversationsCount: response.data?.data?.conversations?.length,
    });
    return response.data.data.conversations || [];
  }

  /**
   * Rename an existing conversation. title-only -- neither of a
   * conversation's two discipline fields is part of this request; the
   * lookup is scoped by project (from the URL) and conversationId alone.
   * @param {string} projectId
   * @param {string} conversationId
   * @param {string} title
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated conversation object
   */
  static async renameConversation(projectId, conversationId, title, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations/${conversationId}`,
      { title }
    );
    return response.data.data.conversation;
  }

  /**
   * Change an existing conversation's CURRENT retrieval scope only: a
   * specific discipline, or project-wide (searchScopeDisciplineId ===
   * null). Applies to subsequent questions asked in this conversation from
   * this point forward. Never changes the conversation's separate,
   * immutable initial classification (disciplineId).
   * @param {string} projectId
   * @param {string} conversationId
   * @param {string|null} searchScopeDisciplineId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated conversation object
   */
  static async updateConversationScope(
    projectId,
    conversationId,
    searchScopeDisciplineId,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations/${conversationId}/scope`,
      { searchScopeDisciplineId }
    );
    return response.data.data.conversation;
  }

  /**
   * Get one conversation and its messages.
   * @param {string} projectId
   * @param {string} conversationId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{conversation: Object, messages: Array}>}
   */
  static async getMessages(projectId, conversationId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations/${conversationId}/messages`
    );
    return {
      conversation: response.data.data.conversation,
      messages: response.data.data.messages || [],
    };
  }

  /**
   * Ask a question within an existing conversation. FieldTime derives
   * request_environment/request_id/history AND the search scope
   * server-side (always the conversation's own currently-stored
   * searchScopeDisciplineId, never its separate immutable disciplineId) --
   * this call sends only the question text (and, optionally, an explicit
   * tool selection); no discipline field is ever part of this request body.
   * @param {string} projectId
   * @param {string} conversationId
   * @param {string} question
   * @param {string} token - JWT authentication token
   * @param {string|null} [tool] - an explicit Schedule Tool selection made
   *   through the chat composer's tool picker (ChatToolPicker.js), one of
   *   CHAT_TOOL_OPTIONS' own ids. Omitted/null preserves the existing
   *   purely natural-language-driven behavior exactly -- see
   *   assistant-controller.js's askQuestion docstring for how the two
   *   converge server-side.
   * @returns {Promise<{userMessage: Object, assistantMessage: Object, conversationTitle: (string|null)}>}
   *   conversationTitle is present only when this question just auto-named a
   *   previously untitled conversation (its first successfully answered
   *   question); otherwise null.
   */
  static async askQuestion(
    projectId,
    conversationId,
    question,
    token,
    tool = null
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/ai/project-docs/projects/${projectId}/documents/assistant/conversations/${conversationId}/query`,
      tool ? { question, tool } : { question }
    );
    return {
      userMessage: response.data.data.userMessage,
      assistantMessage: response.data.data.assistantMessage,
      conversationTitle: response.data.data.conversationTitle || null,
    };
  }
}

export default AssistantAPI;
